import { Box, CircularProgress } from "@mui/material";
import { InnerWrapper, MainContainer, TitleAndActionBox } from "../../common";
import walletBalanceBoxStyles from "./WalletBalanceBox/WalletBalanceBox.module.css";
import WalletBalanceSection from "./WalletBalanceSection";
import InvoicePromoBanner from "./InvoicePromoBanner";
import styles from "./WalletMain.module.css";
import EarnMoney from "./EarnMoney";
import { useContext, useEffect } from "react";
import { LanguageContext } from "../../../context/LanguageContext";
import { HeadingText, UBButton } from "../../../Libraries";
import { useNavigate } from "react-router-dom";
import Transactions from "./AllTransaction/Transactions";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import { useAllAccountsList } from "../../../hooks/useAccount/accountsList";
import { initialFilters } from "../Account/FilterModal";
import { useAuthContext } from "../../../context/AuthContext";
import { formatNum } from "../../../oscar-pos-core/constants";
import { useWalletContext } from "../../../context/WalletContext";
import { Grid } from "@mui/material";
import NonIdealNew from "../../common/NonIdealNew";
import { AccountLimitIcon } from "../../../assets/icons/icon";

const WalletMainScreen = () => {
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const { business } = useAuthContext();
  const {
    walletData,
    walletDataIsLoading,
    earnMoney,
    transactions,
    transactionIsLoading,
  } = useWalletContext();
  const balance = walletData?.current_balance || 0;
  const raastDetails = walletData?.account_info?.raast_info?.raast;
  const isEmiVerified =
    walletData?.account_info?.emi_onboarding_status === "VERIFIED";

  const { handleGetPendingAmountList } = useAllAccountsList();

  const increaseButtonDisable =
    walletData?.account_info?.agent_status !== "NO-REQUEST";
  useEffect(() => {
    handleGetPendingAmountList({
      business,
      startPage: 0,
      endPage: 5,
      searchText: "",
      filters: initialFilters,
    });
  }, []);

  const handleLoadMoney = () => {
    triggerLogEvent(analytics, "w_load_money");
    navigate("/UCash/LoadMoney");
  };
  const handleSendMoney = () => {
    triggerLogEvent(analytics, "w_send_money");
    navigate("/UCash/SendMoney");
  };
  const handleRequestMoney = () => {
    triggerLogEvent(analytics, "w_req_money");
    navigate("/UCash/PendingPaymentCollection", {
      state: { fromDashboard: true },
    });
  };
  const handleShareQR = () => {
    triggerLogEvent(analytics, "w_share_qr");
    navigate("/UCash/ShowQR");
  };
  const handleSendInvoice = () => {
    triggerLogEvent(analytics, "w_send_inv");
    navigate("/InvoiceBook/Create");
  };
  const handleAllTransactions = () => {
    triggerLogEvent(analytics, "w_all_transactions");
    navigate("/UCash/AllTransaction");
  };

  const handleEasyload = (e, item) => {
    e.stopPropagation();
    if (item) triggerLogEvent(analytics, "w_earn_easy_icon");
    else triggerLogEvent(analytics, "w_earn_sell_easy");
    navigate("/UCash/Easyload", { state: { tab: item } });
  };
  const handleBill = (e, item) => {
    e.stopPropagation();
    if (item) triggerLogEvent(analytics, "w_earn_bills_icon");
    else triggerLogEvent(analytics, "w_earn_more_bills");
    navigate("/UCash/BillPayments", {
      state: item ? { billingCompany: item } : {},
    });
  };
  const handleVouchers = (e, item) => {
    e.stopPropagation();
    if (item) triggerLogEvent(analytics, "w_earn_vouchers_icon");
    else triggerLogEvent(analytics, "w_earn_sell_vouchers");
    navigate("/UCash/Vouchers", { state: { tab: item, from: "/UCash" } });
  };
  const handleEasyloadListing = () => {
    navigate("/UCash/EasyloadTransactions");
  };
  const handleBillListing = () => {
    navigate("/UCash/BillTransactions");
  };
  const handleVoucherListing = () => {
    navigate("/UCash/VoucherTransactions");
  };

  const earnMoneyData = {
    earnMoneyList: [
      {
        title: I18n.easyload_packages,
        type: "easyload",
        buttonTitle: I18n.sell_easyload,
        networks: earnMoney.easyloadPackages.list,
        handleClick: handleEasyload,
        handleItemClick: handleEasyloadListing,
      },
      {
        title: I18n.bill_payments,
        type: "bill",
        buttonTitle: I18n.pay_bills,
        networks: earnMoney.billPayments.list,
        handleClick: handleBill,
        handleItemClick: handleBillListing,
      },
      ...(earnMoney.vouchers.list?.length > 0
        ? [
            {
              title: I18n.vouchers,
              type: "voucher",
              buttonTitle: I18n.sell_vouchers,
              networks: earnMoney.vouchers.list,
              handleClick: handleVouchers,
              handleItemClick: handleVoucherListing,
            },
          ]
        : []),
    ],
  };

  const isIdealTransactions = transactions.length > 0;

  if (walletDataIsLoading || walletData === undefined) {
    return (
      <MainContainer fluid>
        <Grid container>
          <Grid item md={12}>
            <TitleAndActionBox
              data={{ title: business?.name, isTitleCase: false }}
            />
            <InnerWrapper className={styles.walletMainWrapper}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="300px"
              >
                <CircularProgress size={30} />
              </Box>
            </InnerWrapper>
          </Grid>
        </Grid>
      </MainContainer>
    );
  }

  return (
    <MainContainer fluid className={styles.walletMainContainer}>
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: I18n.home,
              isTitleCase: false,
            }}
          />
          <InnerWrapper className={styles.walletMainWrapper}>
            <Box
              className={styles.walletTopSection}
            >
              <Box className={walletBalanceBoxStyles.walletBalanceBoxAction}>
                <UBButton
                  title={I18n.add_money}
                  id="LoadMoney"
                  onClick={handleLoadMoney}
                  className="ubButtonStyle ubPrimaryButton textUppercase"
                />
                <UBButton
                  title={I18n.send_money}
                  id="SendMoney"
                  onClick={handleSendMoney}
                  className="ubButtonStyle ubPrimaryButton textUppercase"
                />
                <UBButton
                  title={I18n.collect_money}
                  id="RequestMoney"
                  onClick={handleRequestMoney}
                  className="ubButtonStyle ubSecondaryButton textUppercase"
                />
                {isEmiVerified && (
                  <UBButton
                    title={I18n.share_qr}
                    id="ShareQR"
                    onClick={handleShareQR}
                    className="ubButtonStyle ubSecondaryButton textUppercase"
                  />
                )}
              </Box>

              <InvoicePromoBanner
                className={styles.invoicePromoBanner}
                onCreateInvoice={handleSendInvoice}
              />
            </Box>

            {increaseButtonDisable && (
              <Box className={styles.processingLimitIncreaseBox}>
                <span>
                  <AccountLimitIcon />
                </span>
                <span className={styles.processingLimitIncreaseText}>
                  {I18n.processing_limit_increase_contact_you}
                </span>
              </Box>
            )}

            <WalletBalanceSection
              balance={formatNum(Math.floor(balance || 0))}
              moneyIn={walletData?.incoming_this_month}
              moneyOut={walletData?.outgoing_this_month}
              raastDetails={raastDetails}
            />

            <EarnMoney data={earnMoneyData} />

            <Box className={styles.tranactionListingWrapper}>
              <Box className={styles.tranactionListingTop}>
                <HeadingText tag="h3" className={styles.transactionsTitle}>
                  {I18n.transactions}
                </HeadingText>
                {isIdealTransactions ? (
                  <button
                    id="wallet_see_more_btn"
                    type="button"
                    className={styles.seeMoreLink}
                    onClick={handleAllTransactions}
                  >
                    {I18n.see_more}
                  </button>
                ) : null}
              </Box>

              {transactionIsLoading ? (
                <Box className={styles.transactionListLoadingBox}>
                  <CircularProgress size={25} />
                </Box>
              ) : isIdealTransactions ? (
                <Transactions
                  fromWalletDashboard={true}
                  isLoading={transactionIsLoading}
                  list={transactions}
                />
              ) : (
                <Box className={styles.transactionListNonIdealWrapper}>
                  <NonIdealNew
                    title={I18n.no_transactions_yet}
                    subtitle={I18n.Youll_see_your_all}
                    button1Title="add_money"
                    onButton1Click={handleLoadMoney}
                    loading={false}
                    disabledOne={false}
                    buttonOneId="add_money"
                  />
                </Box>
              )}
            </Box>
          </InnerWrapper>
        </Grid>
      </Grid>
    </MainContainer>
  );
};

export default WalletMainScreen;
